import React, { useState, useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import Section from 'components/Displays/Section';
import CreateAccountWizard from 'components/CreateAccountForm/CreateAccountWizard';
import CompletedAccount from 'components/CreateAccountForm/CompletedAccount';
import ProblematicAccount from 'components/CreateAccountForm/ProblematicAccount';
import { EmailVerify, PhoneVerify } from 'components/CreateAccountForm/Verify';
import {
    joinTypes, updateEmailAddress, updatePhoneNumber, submitParticipantAccount,
    checkAccountConflict, checkIsNotDuplicateUsername
} from 'api/account';
import {
    useBearerTokenContext, useTenantContext, usePublicURLContext,
    useReturnURLContext, useReCaptchaSiteKeyContext
} from 'utilities/contexts';
import { RECAPTCHA_ACTIONS } from 'utilities/constants';
import { useCreateAccountContext } from 'utilities/contexts.createAccount';
import { useForm } from 'react-hook-form';
import './AppLayout.scss';

// validation config
// defined outside the component because this is static and to easily use in tests
const REQUIRED_FIELD = { required: 'Required field' };
const MAX_LENGTH_100 = { maxLength: { value: 100, message: 'Maximum length is 100 characters' } };
const MAX_LENGTH_250 = { maxLength: { value: 250, message: 'Maximum length is 250 characters' } };
const DAY = { min: { value: 1, message: 'Must be a valid day' }, max: { value: 31, message: 'Must be a valid day' } };
const MONTH = { min: { value: 1, message: 'Must be a valid month' }, max: { value: 12, message: 'Must be a valid month' } };
const YEAR = { minLength: { value: 4, message: 'Must be a valid year' }, maxLength: { value: 4, message: 'Must be a valid year' } };

export const VALIDATION_SCHEMA = {
    firstName: { ...REQUIRED_FIELD, ...MAX_LENGTH_100 },
    lastName: { ...REQUIRED_FIELD, ...MAX_LENGTH_100 },
    day: { ...REQUIRED_FIELD, ...DAY },
    month: { ...REQUIRED_FIELD, ...MONTH },
    year: {
        ...REQUIRED_FIELD,
        ...YEAR
    },
    dateOfBirth: {
        validate: {
            beforeToday: (v, formValues) => {
                if (formValues.day && formValues.month && formValues.year) {
                    const dobDate = `${formValues.year.padStart(4, "0")}-${formValues.month.padStart(2, "0")}-${formValues.day.padStart(2, "0")}`;
                    const todayDate = new Date();
                    if (Date.parse(dobDate) >= todayDate.setUTCHours(0, 0, 0, 0))
                        return 'Date of Birth must be before today'
                }
                return true;
            },
            after1900: (v, formValues) => {
                if (formValues.day && formValues.month && formValues.year) {
                    const dobDate = `${formValues.year.padStart(4, "0")}-${formValues.month.padStart(2, "0")}-${formValues.day.padStart(2, "0")}`;
                    const date1900 = new Date('1900-01-01');
                    if (Date.parse(dobDate) <= Date.parse(date1900))
                        return 'Date of Birth must be after 1900'
                }
                return true;
            },
            validDate: (v, formValues) => {
                if (formValues.day && formValues.month && formValues.year) {
                    const dobString = `${formValues.year.padStart(4, "0")}-${formValues.month.padStart(2, "0")}-${formValues.day.padStart(2, "0")}T00:00:00`;

                    // NaN is unlikely.
                    if (isNaN(Date.parse(dobString)))
                        return 'Date of Birth must be a valid date'
                    else {
                        // A date like 2023-02-29 is parsed to 2023-03-01.
                        // Check parsed date pieces to ensure day falls in desired month.
                        const dobDate = new Date(dobString);

                        const dayMatch = Number(formValues.day);
                        const monthMatch = Number(formValues.month) - 1;
                        const yearMatch = Number(formValues.year);

                        if (dobDate.getFullYear() !== yearMatch || dobDate.getMonth() !== monthMatch || dobDate.getDate() !== dayMatch)
                            return 'Date of Birth must be a valid date'
                    }
                }
                return true;
            }
        }
    },
    pronounGuids: { ...REQUIRED_FIELD },
    parentFirstName: { ...REQUIRED_FIELD, ...MAX_LENGTH_100 },
    parentLastName: { ...REQUIRED_FIELD, ...MAX_LENGTH_100 },
    emailAddress: {
        ...REQUIRED_FIELD,
        ...MAX_LENGTH_250,
        validate: {
            email: (v) => {
                // use built-in HTML5 validation instead of regex, but we could use a regex instead
                // we're manually calling this because the form is set to novalidate (to centralize all error handling)
                const input = document.createElement('input');
                input.type = 'email';
                input.value = v;
                return input.checkValidity() || 'Invalid email format: Please enter a valid email address (e.g. john@example.com)';
            }
        }
    },
    phoneNumber: {
        ...REQUIRED_FIELD,
        pattern: { value: /^\d{3}-\d{3}-\d{4}$/, message: 'Invalid format: Please enter a valid phone number in the format 123-456-7890' },
        setValueAs: inputValue => 
        {
            if (inputValue != null) {
                let x = inputValue.replace(/(\d{3})-?(\d{3})-?(\d{4})(.*)/, '$1-$2-$3$4');
                return x;
            }
            return inputValue;
        }
    },
    phoneNumberTypeGuid: { ...REQUIRED_FIELD },
    zipCode: {
        ...REQUIRED_FIELD,
        pattern: { value: /^\d{5}(-\d{4})?$/, message: 'Invalid format: Please enter a valid zip code (e.g. 12345 or 12345-6789)' }
    },
    parentPronounGuids: { ...REQUIRED_FIELD },
    userName: {
        ...REQUIRED_FIELD,
        ...MAX_LENGTH_250,
        pattern: { value: /^[a-zA-Z0-9._\-+@]+$/, message: 'Invalid character: Allowed characters include letters, numbers and ._-+@' }
        //Assignment moved to CreateAccountWizard so that checkForDuplicateUsername
        //can reference server error handling functions.
        //validate: async (v) => {
        //    return await checkForDuplicateUsername(v) || 'An account using this Username already exists. Please pick a different Username.';
        //}
    },
    password: {
        ...REQUIRED_FIELD,
        ...MAX_LENGTH_100,
        minLength: { value: 8, message: 'Must be at least 8 characters long' },
        validate: {
            number: (v) => /\d/.test(v) || 'Must have at least one number',
            special: (v) => /[~!@#$%^&()\-=+]/.test(v) || 'Must have at least one special character from the following list ~!@#$%^&()-=+'
        }
    },
    confirmPassword: {
        ...REQUIRED_FIELD,
        validate: (v, formValues) => {
            return v === formValues.password || 'Your password does not match the password in the previous password field.';
        }
    },
    genderGuid: { ...REQUIRED_FIELD },
    genderIdentityGuid: { ...REQUIRED_FIELD },
    raceGuids: { ...REQUIRED_FIELD },
    ethnicityGuid: { ...REQUIRED_FIELD },
    phoneCode: { ...REQUIRED_FIELD },
    emailCode: {
        ...REQUIRED_FIELD,
        pattern: { value: /^\d{6}$/, message: 'Invalid format: A valid code is 6 digits' } },
    channel: { ...REQUIRED_FIELD }
};


// accountCreateSettings:   Synamic options (race, gender, etc) and misc (help #, date of 18)
//                          See RF.Core.API.Models.AccountCreateSettings
// participantPerson:  Existing participant information (i.e. JoinFromGuid)
//                      See RF.Core.Data.TransferObjects.ParticipantPerson

export default function AppLayout({ accountCreateSettings, participantPerson }) {

    const bearer = useBearerTokenContext();
    const tenant = useTenantContext();
    const publicURL = usePublicURLContext();
    const returnURL = useReturnURLContext();
    const createAccountContext = useCreateAccountContext();
    const reCaptchaSiteKey = useReCaptchaSiteKeyContext()
    const joinType = createAccountContext.joinType; //Join, JoinToMerge, JoinFromGuid
    const thirdPartyUserName = createAccountContext.thirdPartyUserName;

    // Default Join heading/title text
    let heroHeading = <>Join Pitt+Me<sup><small>&reg;</small></sup></>;
    let heroMessage = "Become a part of a growing community of over 300,000 participants dedicated to furthering the goals of research and improving health care.";
    const heroImageBasePath = `${process.env.REACT_APP_API_ROOT_URL}/images/pittplusme/heros`;
    let pageTitle = "Join To Receive Relevant Research Studies";
    let assistanceMessage = <>For assistance, please call {accountCreateSettings.callCenterNumber}.</>

    // JoinFromGuid
    if (joinType === joinTypes.JoinFromGuid) {
        heroHeading = <>Complete Your Profile</>;
        heroMessage = "to finish setting up your online access";
        pageTitle = "Complete Your Profile";
        // JoinToMerge (i.e. Post Card Join)
    } else if (joinType === joinTypes.JoinToMerge) {
        heroHeading = <>Create Your Online Pitt+Me<sup><small>&reg;</small></sup> Account</>
        heroMessage = "An online Pitt+Me account allows you to personalize your health interests, get customized study opportunities by email, and receive our digital newsletter.";
        pageTitle = "Create Account To Receive Relevant Research Studies";
        //Study Context 
    } if (returnURL !== "") {
        heroMessage = "Thank you for your interest in this study!  It looks like you haven't joined Pitt+Me.  Please fill out the information below to create your account.";
        assistanceMessage = <>If you would like to participate in this study without registering, please call {accountCreateSettings.callCenterNumber}.</>;
    }

    // Error messaging
    const [errorMessage, setErrorMessage] = useState(null);

    // Should be called only once.
    useEffect(() => {
        document.title = pageTitle;
    }, [pageTitle]);


    // form config and default values
    debugger;
    const defaultDOB = participantPerson ? new Date(participantPerson.dateOfBirth + 'T00:00:00') : new Date();
    const defaultPronoun = participantPerson?.pronounGuids?.length ? participantPerson.pronounGuids : ['3B4D3EE8-16D8-4209-B260-522A4E0708F9']; //RF-1815 hide field, use placeholder value;
    const defaultParentPronoun = participantPerson?.parentPronounGuids?.length ? participantPerson.parentPronounGuids : ['3B4D3EE8-16D8-4209-B260-522A4E0708F9']; //RF-1815 hide field, use placeholder value;

    const { register, handleSubmit, watch, setValue, getValues, setError, clearErrors, reset, resetField, trigger, formState, formState: { errors } } = useForm({
        defaultValues: participantPerson ? {
            ...participantPerson, day: `${defaultDOB.getDate()}`, month: `${defaultDOB.getMonth() + 1}`, year: `${defaultDOB.getFullYear()}`, pronounGuids: defaultPronoun, parentPronounGuids: defaultParentPronoun   } :
            {
                timeZoneInt: '1',
                hasAccount: false,
                isCommunity: true,
                emailVerified: false,
                phoneVerified: false,
                isThirdPartyUserName: thirdPartyUserName ? true : false,
                joinToMerge: joinType === joinTypes.JoinToMerge,
                participantGuid: null,
                firstName: null,
                lastName: null,
                day: null,
                month: null,
                year: null,
                dateOfBirth: null,
                isPediatric: false,
                pronounGuids: ['3B4D3EE8-16D8-4209-B260-522A4E0708F9'], //RF-1815 hide field, use placeholder value
                parentFirstName: null,
                parentLastName: null,
                emailAddress: null,
                emailAddressGuid: null,
                phoneNumber: null,
                phoneNumberGuid: null,
                phoneNumberTypeGuid: null,
                zipCode: null,
                addressGuid: null,
                parentPronounGuids: ['3B4D3EE8-16D8-4209-B260-522A4E0708F9'], //RF-1815
                userName: thirdPartyUserName,
                password: null,
                confirmPassword: null,
                genderGuid: null,
                genderIdentityGuid: 'DBAFC9E1-4945-4CAF-8961-D187CFA80C0B', //RF-1815
                raceGuids: [],
                ethnicityGuid: null,
                phoneCode: null,
                channel: null,
                preferenceGuids: []
            },
        mode: 'onChange',
        criteriaMode: 'all',
        delayError: 1500

    });


    // subscribe to form data that we need to regularly read elsewhere
    const [day, month, year, isPediatric, firstName, userName, isThirdPartyUserName, hasAccount, isCommunity, phoneVerified, emailVerified, emailAddress, phoneNumber, channel]
        = watch(['day', 'month', 'year', 'isPediatric', 'firstName', 'userName', 'isThirdPartyUserName', 'hasAccount', 'isCommunity', 'phoneVerified', 'emailVerified', 'emailAddress', 'phoneNumber', 'channel']);

    // coordinate DOB and individual date inputs, isPediatric status
    useEffect(() => {
        if (day && month && year) {
            // update hidden DOB with values from date inputs when we have them
            const value = `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            clearErrors('dateOfBirth');
            setValue('dateOfBirth', value, { shouldDirty: true });

            // See if we need to update isPediatric            
            if (joinType !== joinTypes.JoinFromGuid) { // When JoinFromGuid, prevent dateOfBirth from changing adult/pediatric
                const mSecDob = Date.parse(value);
                if (mSecDob > accountCreateSettings.msecOf18Years) {
                    setValue('isPediatric', true, { shouldDirty: true });
                } else if (mSecDob < accountCreateSettings.msecOf18Years) {
                    setValue('isPediatric', false, { shouldDirty: true });
                }
            }
        }
    }, [day, month, year, accountCreateSettings.msecOf18Years, setValue, joinType, clearErrors]);


    const showErrorMessage = (message, includeTemplate) => {
        const templateText = ` Please try again. If the problem persists contact us for assistance at ${accountCreateSettings.callCenterNumber}.`
        if (!message) {
            message = 'A network or unexpected error has occurred.'
            includeTemplate = true;
        }

        if (includeTemplate)
            message += templateText;

        setErrorMessage(message);
    }

    const clearErrorMessage = () => {
        if (errorMessage)
            setErrorMessage(null);
    }

    const handleServerErrors = (response, isWizard) => {

        if (response?.errors) {
            Object.keys(response.errors).forEach(field => {
                const name = `${field[0].toLowerCase()}${field.slice(1)}`;
                response.errors[field].forEach(message => setError(name, { type: 'server', message: message }));
            });

            if (isWizard)
                showErrorMessage('Invalid entry.  Please return to the Basic Details step and review all step entries.', false);
            else
                showErrorMessage('Invalid entry.', false);
        }
        else if (response?.ApplicationExceptions) {
            showErrorMessage(response.ApplicationExceptions[0], false);
        }
        else {
            console.log(response);
            showErrorMessage();
        }
    }

    // dup username field check
    const checkForDuplicateUsername = (value) => {

        const duplicateCheckData = new FormData();
        duplicateCheckData.append("UserName", value);

        return checkIsNotDuplicateUsername(duplicateCheckData, publicURL).then(
            (successResponse) => {
                clearErrorMessage();
                return successResponse;
            },
            (rejectedResponse) => {
                handleServerErrors(rejectedResponse, true);
                return false;
            });
    };

    // dup check validation for email field
    const checkForAccountConflict = () => {
        return new Promise((resolve) => {
            // No need to check for duplicates for JoinToMerge and JoinFromGuid
            if (joinType !== joinTypes.Join) {
                return resolve(true);
            }

            const [dateOfBirth, lastName] = getValues(['dateOfBirth', 'lastName']);

            const duplicateCheckData = {
                "dateOfBirth": dateOfBirth,
                "emailAddress": emailAddress,
                "lastName": lastName,
                "firstName": firstName,
                "isPediatric": isPediatric,
                "phoneNumber": phoneNumber
            };

            // return until we have all the data we need to check account
            if (Object.values(duplicateCheckData).some(value => value === null)) 
                return resolve(false);

            checkAccountConflict(duplicateCheckData, bearer, tenant).then(
                (successResponse) => {
                    clearErrorMessage();
                    if (successResponse?.statusCode && successResponse?.message) {
                        if (successResponse.statusCode === "OK") {
                            return resolve(true);
                        }
                        else {
                            handleAccountErrors(successResponse.statusCode, successResponse.message);
                            return resolve(false);
                        }
                    }
                    showErrorMessage(successResponse);
                    return resolve(false);
                },
                (rejectedResponse) => {
                    handleServerErrors(rejectedResponse, true);
                    return resolve(false);
                }
            );
        });
    }

    const onCompleteContactInfo = () =>
    {
        return checkForAccountConflict().then((successResponse) => {
            if (successResponse === false)
                return false;

            if (!userName && emailAddress) {
                var result = checkForDuplicateUsername(emailAddress);
                if (result !== false) { 
                    result.then((successResponse) => { if (successResponse) { setValue('userName', emailAddress); } } );                    
                };
            }

            return true;            
        });
    }

    // final submit functions for form/wizard
    // async and awaits used to ensure formState.isSubmitting true throughout
    // token generation and post.
    const onSubmit = async (data) => {

        // DoubleClick prevention
        if (formState.isSubmitting)
            return;

        //console.log(data); // testing

        try {
            let recaptchaResult = await getRecaptchaToken();
            if (recaptchaResult.enabled === true) {
                data.recaptchauserreponsetoken = recaptchaResult.token;
            }

            return await completeOnSubmit(data);

        } catch (e) {
            showErrorMessage(e, true);
            return false;
        }
    }

    const completeOnSubmit = (data) => {
        return new Promise((resolve) => {
            submitParticipantAccount(data, joinType, bearer, tenant).then(
                (successResponse) => {
                    // reset form with values from server, also updates form defaultValues
                    clearErrorMessage();
                    reset(successResponse);

                    return resolve(true);
                },
                (rejectedResponse) => {
                    if (rejectedResponse?.errorCode && rejectedResponse?.message) {
                        // account errors
                        handleAccountErrors(rejectedResponse.errorCode, rejectedResponse.message);
                    } else {
                        handleServerErrors(rejectedResponse, true);
                    }
                    return resolve(false);
                }
            );
        });
    }

    // determine if reCaptcha is enabled.  if so, provide token.
    // if enabled but blocked/broken, reject with message.
    const getRecaptchaToken = () => {
        return new Promise((resolve, reject) => {

            if (reCaptchaSiteKey !== "" &&
                typeof window !== 'undefined') {

                if (typeof window.grecaptcha === 'undefined' ||
                    typeof window.grecaptcha.render !== 'function') {
                    return reject('Unable to load reCAPTCHA fraud prevention.');
                }

                window.grecaptcha.ready(function () {
                    window.grecaptcha.execute(reCaptchaSiteKey, { action: RECAPTCHA_ACTIONS.Join }).then(function (token) {
                        return resolve({ enabled: true, token: token });
                    });
                });
            }
            else {
                return resolve({ enabled: false, token: null });
            }
        });
    }

    // check dirty status of field in formState
    const fieldIsDirty = name => formState.dirtyFields[name] || false;

    // handle requests to update email or phone (defined here because multiple components share the logic)
    const fieldUpdate = (newValue, newValueProperty, fieldName, fieldLabel) => {

        const handleSuccess = () => {
            clearErrorMessage();
            resetField(fieldName, { defaultValue: newValue });
        };
        const handleReject = (rejectedResponse) => {
            handleServerErrors(rejectedResponse, false);
        };

        // make sure that the field is valid before continuing 
        trigger(fieldName).then(triggerResult => {
            if (triggerResult === false) return;

            const [guid] = getValues([`${fieldName}Guid`]);
            const change = { [newValueProperty]: newValue, [`${fieldName}Guid`]: guid };

            if (fieldName === 'emailAddress') {
                updateEmailAddress(change, bearer, tenant).then(handleSuccess, handleReject);
            }
            if (fieldName === 'phoneNumber') {
                updatePhoneNumber(change, bearer, tenant).then(handleSuccess, handleReject);
            }
        });
    }

    // problematic account
    const [ accountError, setAccountError ] = useState({ errorCode: null, errorMessage: null, recover: false });

    const handleAccountErrors = (errorCode, message) => {

        const accountError = { errorCode: errorCode, errorMessage: message };

        if (errorCode === "BlockedByCaptcha")
            document.location = publicURL + "?SavedAlert=" + encodeURIComponent("Successfully sent!");
        else if (errorCode === "ExistingAccountRecover")
            setAccountError({ ...accountError, recover: true });
        else
            setAccountError({ ...accountError, recover: false });
    }

    // determine content to display
    const contentType = {
        Create: 'Create',
        Verify: 'Verify',
        Completed: 'Completed',
        Problem: 'Problem'
    };

    let currentContent = contentType.Create;
    if (accountError.errorCode) {
        currentContent = contentType.Problem;
    } else if (!hasAccount) {
        currentContent = contentType.Create;
    } else if (hasAccount && (!emailVerified || !phoneVerified)) {
        currentContent = contentType.Verify;
    } else if (hasAccount && emailVerified && phoneVerified) {
        currentContent = contentType.Completed;
    }

    const errorMessageBox = (
        <div className='error-box mx-sm-5 mb-4' role="alert">
            {errorMessage}
        </div>);

    /* Log-in message limted to account create content */
    let superSectionMessage = (<span className='d-flex justify-content-center mt-5 mb-4' style={{ fontSize: "20px" }}>Already a Pitt+Me member?&nbsp;<a className="nav-link" href={publicURL + "/account/login"}>Log-in</a></span>)
    if (currentContent !== contentType.Create)
        superSectionMessage = (<span className='d-flex justify-content-center mt-5 mb-4' style={{ fontSize: "20px" }}>&nbsp;</span>)

    return (
        <div>
            <div className='hero'>
                <img alt=""
                    srcSet={`${heroImageBasePath}/pittsburgh.jpg, ${heroImageBasePath}/pittsburgh@2x.jpg 2x, ${heroImageBasePath}/pittsburgh@3x.jpg 3x`}
                    src={`${heroImageBasePath}/pittsburgh.jpg`} />
                <div className="hero-overlay">
                    <div className="container">
                        <h1>{heroHeading}</h1>
                        <p>{heroMessage}</p>
                    </div>
                </div>
            </div>
            
            {superSectionMessage}
            
            <div aria-live="polite">
                {currentContent === contentType.Create &&
                    <div className="d-flex justify-content-center">
                        <div className="contentSize">
                            <Section className='blue'>

                                {errorMessage && errorMessageBox}

                                <Form noValidate>
                                    <CreateAccountWizard
                                        accountCreateSettings={accountCreateSettings}
                                        errors={errors} register={register} setValue={setValue} getValues={getValues} trigger={trigger} fieldIsDirty={fieldIsDirty}
                                        onSubmit={handleSubmit(onSubmit)} onCompleteContactInfo={onCompleteContactInfo} checkForDuplicateUsername={checkForDuplicateUsername}
                                        isPediatric={isPediatric} isThirdPartyUserName={isThirdPartyUserName} firstName={firstName} joinType={joinType}
                                        isSubmitting={formState.isSubmitting} 
                                    />
                                </Form>

                                <div className='d-flex justify-content-center assistance-instructions'>
                                    {assistanceMessage}
                                </div>
                            </Section>
                        </div>
                    </div>
                }

                {currentContent === contentType.Verify &&
                    <div className="d-flex justify-content-center">
                        <div className="contentSize">
                            <Section className='blue'>

                                {errorMessage && errorMessageBox}

                                <div className='d-flex justify-content-center mt-3'>
                                    <img src={`${process.env.REACT_APP_API_ROOT_URL}/images/icon_envelope.svg`} alt="" style={{ width: "135px" }} />
                                </div>
                                <div className='d-flex justify-content-center'><h2 className='my-4 text-center fs-1'>You're almost done...</h2></div>

                                {/*Community participants have 2-step verification.  UPMC Participants have only email verification and then only if it has changed.*/}
                                <div className='d-flex justify-content-center'>
                                    <div className='w-75 text-center'>Please complete the{isCommunity ? " 2-step " : " "}account verification process below. Then you can start browsing for studies!</div>
                                </div>

                                <hr className="my-5" />

                                <EmailVerify
                                    errors={errors} register={register}
                                    getValues={getValues} resetField={resetField} setError={setError} fieldUpdate={fieldUpdate} setValue={setValue}
                                    emailVerified={emailVerified} emailAddress={emailAddress} emailIsDirty={fieldIsDirty('emailAddress')} isCommunity={isCommunity}
                                    trigger={trigger} clearErrorMessage={clearErrorMessage} handleServerErrors={handleServerErrors}
                                />

                                {!phoneVerified &&
                                    <PhoneVerify
                                        errors={errors} register={register}
                                        getValues={getValues} resetField={resetField} setError={setError}
                                        fieldUpdate={fieldUpdate} setValue={setValue} trigger={trigger}
                                        phoneNumber={phoneNumber} phoneIsDirty={fieldIsDirty('phoneNumber')} emailVerified={emailVerified} channel={channel} isCommunity={isCommunity}
                                        initialAttempts={participantPerson?.phoneVerification?.attempts > 0 ? participantPerson.phoneVerification.attempts : 0}
                                        handleServerErrors={handleServerErrors} clearErrorMessage={clearErrorMessage}
                                    />
                                }

                                <div className='d-flex justify-content-center mt-5'>
                                    <div className="text-center terms-of-service w-75">
                                        By clicking "Send Code Now" you're agreeing to the <a href="https://ctsi.pitt.edu/terms-of-use#mobile/">Mobile Terms of Service</a> to verify your phone number via SMS messaging or voice.
                                    </div>
                                </div>

                                <div className='d-flex justify-content-center assistance-instructions mt-5'>
                                    For assistance, please call {accountCreateSettings.callCenterNumber}.
                                </div>

                            </Section>
                        </div>
                    </div>
                }

                {currentContent === contentType.Completed &&
                    <div className="d-flex justify-content-center">
                        <div className="contentSize">
                            <Section className='blue'>
                                <CompletedAccount />
                            </Section>
                        </div>
                    </div>
                }

                {/*Problematic Account Messages*/}
                {currentContent === contentType.Problem &&
                    <div className="d-flex justify-content-center">
                        <div className="contentSize">
                            <Section className='blue'>
                                <ProblematicAccount emailAddress={emailAddress} message={accountError.errorMessage} recover={accountError.recover} />
                            </Section>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
}
